import React, { useCallback, useEffect } from 'react'

import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi2'
import { toast } from 'sonner'

import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import Spinner from '@/components/common/Spinner'
import { Tabs, TabsList, TabsTrigger } from '@/components/common/Tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { SalesforceIcon } from '@/components/integrations/Salesforce/Icon'
import { SalesforceFieldInput } from '@/components/integrations/Salesforce/SalesforceFieldInput'

import {
  useLatestPursuitSchema,
  useLatestPursuitSchemaId,
} from '@/hooks/pursuits/schemas/usePursuitSchemas'
import type {
  SalesforceField,
  SalesforceFieldType,
} from '@/hooks/salesforce/types'
import { useSalesforceIntegration } from '@/hooks/salesforce/useSalesforceIntegration'
import { useSalesforceOpportunityFields } from '@/hooks/salesforce/useSalesforceOpportunityFields'
import { useSalesforceStageMappings } from '@/hooks/salesforce/useSalesforceStageMappings'
import { useUpdateSalesforceStageMappings } from '@/hooks/salesforce/useUpdateSalesforceStageMappings'

import type { ExtendedSchemaDerivedField } from '@/types/extended-schema'
import type { UUID } from '@/types/Id'
import type { FieldMapping } from '@/types/Integration'

type Mode = 'pursuit_field' | 'override'

interface MappingEntry {
  mode: Mode
  pursuitFieldId?: string
  overrideValue?: string | number | boolean | null
}

type FieldMappingsFormValues = Record<string, MappingEntry>

interface FormValues {
  fieldMappings: FieldMappingsFormValues
}

interface FieldMapperProps {
  orgId: UUID
}

const CLEAR_VALUE = '_CLEAR_'

const IGNORED_SF_FIELDS = new Set<string>([
  'Id',
  'StageName', // Managed in Stage Mapper
  'Name',
  'CloseDate',
  'Description',
])
const UNSUPPORTED_TYPES = new Set<SalesforceFieldType>(['reference'])

interface FieldOption {
  id: string
  label: string
}

const STATIC_FIELDS: FieldOption[] = [
  { id: 'title', label: 'Title' },
  { id: 'description', label: 'Description' },
  { id: 'due_date', label: 'Due Date' },
  { id: 'estimated_value', label: 'Estimated Value' },
  { id: 'stage', label: 'Stage' },
]

interface CombinedOptions {
  staticOptions: FieldOption[]
  dynamicOptions: FieldOption[]
}

const useBuildCombinedOptions = (
  schemaFields: ExtendedSchemaDerivedField[] | undefined,
): CombinedOptions => {
  const dynamicOptions =
    (schemaFields || []).map((f) => ({
      id: f.id,
      label: f.name,
    })) ?? []
  return {
    staticOptions: STATIC_FIELDS,
    dynamicOptions,
  }
}

const selectMappableSalesforceFields = (
  data: { fields: SalesforceField[] } | undefined,
): SalesforceField[] => {
  if (!data) return []
  return (data.fields || [])
    .filter(
      (f) =>
        f.createable &&
        !IGNORED_SF_FIELDS.has(f.name) &&
        !UNSUPPORTED_TYPES.has(f.type),
    )
    .sort((a, b) => a.label.localeCompare(b.label))
}

interface MappingModeToggleProps {
  value: Mode
  onChange: (v: Mode) => void
}

const tabTriggerClasses =
  'flex-1 text-[13px] data-[state=active]:bg-accent-500 dark:data-[state=active]:bg-accent-500 data-[state=active]:text-white dark:data-[state=active]:text-white'

const MappingModeToggle: React.FC<MappingModeToggleProps> = ({
  value,
  onChange,
}) => {
  const handleValueChange = useCallback(
    (v: string) => {
      if (v === 'pursuit_field' || v === 'override') {
        onChange(v)
      }
    },
    [onChange],
  )
  return (
    <Tabs value={value} onValueChange={handleValueChange} variant="grouped">
      <TabsList className="h-7 w-full">
        <TabsTrigger value="pursuit_field" className={tabTriggerClasses}>
          Pursuit Field
        </TabsTrigger>
        <TabsTrigger value="override" className={tabTriggerClasses}>
          Static Override
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )
}

interface RowProps {
  sfField: SalesforceField
  combinedOptions: CombinedOptions
}

const Row: React.FC<RowProps> = React.memo(({ sfField, combinedOptions }) => {
  const { control, setValue } = useFormContext<FormValues>()
  const current = useWatch({
    control,
    name: `fieldMappings.${sfField.name}` as const,
  }) as MappingEntry | undefined

  const setMode = useCallback(
    (mode: Mode) => {
      setValue(`fieldMappings.${sfField.name}` as const, {
        ...(current || {}),
        mode,
        // when switching modes, clear the other value
        pursuitFieldId:
          mode === 'pursuit_field'
            ? (current?.pursuitFieldId ?? undefined)
            : undefined,
        overrideValue:
          mode === 'override' ? (current?.overrideValue ?? null) : undefined,
      })
    },
    [current, setValue, sfField.name],
  )

  const setPursuitField = useCallback(
    (id: string) => {
      const adjustedId = id === CLEAR_VALUE || id === '' ? undefined : id
      setValue(`fieldMappings.${sfField.name}` as const, {
        ...(current || { mode: 'pursuit_field' }),
        mode: 'pursuit_field',
        pursuitFieldId: adjustedId,
      })
    },
    [current, setValue, sfField.name],
  )

  const setOverride = useCallback(
    (val: string | number | boolean | null) => {
      setValue(`fieldMappings.${sfField.name}` as const, {
        ...(current || { mode: 'override' }),
        mode: 'override',
        overrideValue: val,
      })
    },
    [current, setValue, sfField.name],
  )

  const defaultMode: Mode =
    sfField.type === 'picklist' || sfField.type === 'multipicklist'
      ? 'override'
      : 'pursuit_field'
  const mode: Mode = current?.mode ?? defaultMode

  const isStageName = sfField.name === 'StageName'
  const isUnsupported = UNSUPPORTED_TYPES.has(sfField.type)
  const disabled = isStageName || isUnsupported

  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-6 items-center">
      <div className="col-span-1 md:col-span-2 text-sm font-medium text-primary-900 dark:text-primary-100">
        <div className="flex items-center gap-2">
          <span className="truncate">{sfField.label}</span>
          <Badge
            variant="outline"
            className="text-xs px-1 py-0.5 px-1.5 border-none bg-primary-100 dark:bg-primary-800"
          >
            {sfField.type.charAt(0).toUpperCase() + sfField.type.slice(1)}
          </Badge>
        </div>
      </div>
      <div className="col-span-1 md:col-span-2">
        {disabled ? (
          <div className="flex items-center gap-2 text-sm text-primary-500 dark:text-primary-400">
            <Tooltip delayDuration={0}>
              <TooltipTrigger type="button" className="cursor-default">
                Mapping disabled
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px]" side="right">
                {isStageName
                  ? 'Stage is managed via the Stage Mapper dialog.'
                  : 'This Salesforce field type is not supported.'}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <MappingModeToggle value={mode} onChange={setMode} />
        )}
      </div>
      <div className="col-span-2 md:col-span-2 mb-4 md:mb-0">
        {disabled ? (
          <div className="text-sm text-primary-500 dark:text-primary-400">
            Use the appropriate configuration menu to map this field.
          </div>
        ) : mode === 'pursuit_field' ? (
          (() => {
            const allOptions = [
              ...combinedOptions.staticOptions,
              ...combinedOptions.dynamicOptions,
            ]
            const selectedOption = current?.pursuitFieldId
              ? allOptions.find((opt) => opt.id === current.pursuitFieldId)
              : null
            const isDynamic = selectedOption
              ? combinedOptions.dynamicOptions.some(
                  (opt) => opt.id === selectedOption.id,
                )
              : false

            return (
              <Select
                value={current?.pursuitFieldId || CLEAR_VALUE}
                onValueChange={setPursuitField}
              >
                <SelectTrigger className="w-full h-9">
                  {!selectedOption ? (
                    <span className="text-primary-500 dark:text-primary-400">
                      No mapping
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{selectedOption.label}</span>
                      {isDynamic && (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger>
                            <HiOutlineQuestionMarkCircle
                              size={15}
                              className="text-primary-500 dark:text-primary-400"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[260px]">
                            Dynamic field from your Pursuit Configuration
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CLEAR_VALUE}>
                    <span className="text-primary-500 dark:text-primary-400">
                      No mapping
                    </span>
                  </SelectItem>
                  {combinedOptions.staticOptions.length > 0 && (
                    <>
                      {combinedOptions.staticOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </SelectItem>
                      ))}
                      {combinedOptions.dynamicOptions.length > 0 && (
                        <div className="border-t border-primary-200 dark:border-primary-800 my-1" />
                      )}
                    </>
                  )}
                  {combinedOptions.dynamicOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id} className="w-full">
                      <div className="flex items-center justify-between gap-2 w-full max-w-[356px] truncate">
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger>
                            <HiOutlineQuestionMarkCircle
                              size={15}
                              className="text-primary-500 dark:text-primary-400"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[260px]" side="left">
                            Dynamic field from your Pursuit Configuration
                          </TooltipContent>
                        </Tooltip>
                        <span className="truncate">{opt.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          })()
        ) : (
          <SalesforceFieldInput
            field={sfField}
            value={current?.overrideValue}
            onChange={setOverride}
          />
        )}
      </div>
    </div>
  )
})

Row.displayName = 'Salesforce.FieldMapper.Row'

const FieldMapperBody: React.FC<FieldMapperProps> = ({ orgId }) => {
  const { data: rows = [], isLoading: isLoadingFields } =
    useSalesforceOpportunityFields({
      orgId,
      enabled: true,
      select: selectMappableSalesforceFields,
    })
  const { data: latestSchema } = useLatestPursuitSchema({ orgId })

  const isLoading = isLoadingFields
  const combinedOptions = useBuildCombinedOptions(latestSchema?.fields)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner />
      </div>
    )
  }

  if (!rows.length) {
    return (
      <div className="text-center py-8 bg-primary-50 dark:bg-primary-900 p-3 rounded-md text-sm text-primary-600 dark:text-primary-400">
        No Salesforce fields available for mapping.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 w-full">
      {rows.map((sf) => (
        <Row key={sf.name} sfField={sf} combinedOptions={combinedOptions} />
      ))}
    </div>
  )
}

interface FieldMapperDialogProps {
  orgId: UUID
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const FieldMapperDialog: React.FC<FieldMapperDialogProps> = ({
  orgId,
  open,
  onOpenChange,
}) => {
  const { data: integration } = useSalesforceIntegration({ orgId })
  const { data: fields } = useSalesforceOpportunityFields({
    orgId,
    enabled: true,
  })
  const { data: latestSchemaId } = useLatestPursuitSchemaId({ orgId })
  const { data: stageMappings } = useSalesforceStageMappings({ orgId })
  const { mutateAsync: updateMappings, isPending } =
    useUpdateSalesforceStageMappings({ orgId })

  const form = useForm<FormValues>({
    defaultValues: { fieldMappings: {} },
  })

  const { handleSubmit, reset } = form

  const buildDefaults = useCallback((): FormValues => {
    const mapping: FieldMappingsFormValues = {}
    if (integration?.field_mappings && fields?.fields) {
      for (const fm of integration.field_mappings) {
        // find the SF field by label
        const sf = fields.fields.find(
          (f) => f.label === fm.salesforce_field_label,
        )
        if (!sf) continue
        const nameKey = sf.name
        if (fm.pursuit_extended_schema_field_id) {
          mapping[nameKey] = {
            mode: 'pursuit_field',
            pursuitFieldId: fm.pursuit_extended_schema_field_id,
          }
        } else {
          mapping[nameKey] = {
            mode: 'override',
            overrideValue: (fm.override_value ?? null) as
              | string
              | number
              | boolean
              | null,
          }
        }
      }
    }
    return { fieldMappings: mapping }
  }, [integration?.field_mappings, fields?.fields])

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const sfByName: Record<string, SalesforceField> = {}
      for (const f of fields?.fields || []) {
        sfByName[f.name] = f
      }
      // Build field_mappings payload; only include rows with a selection
      const field_mappings: FieldMapping[] = Object.entries(data.fieldMappings)
        .map(([sfName, entry]) => {
          const sf = sfByName[sfName]
          if (!sf) return null
          if (entry.mode === 'pursuit_field' && entry.pursuitFieldId) {
            return {
              salesforce_field_label: sf.label,
              pursuit_extended_schema_field_id: entry.pursuitFieldId,
              override_value: null,
            } as FieldMapping
          }
          if (entry.mode === 'override') {
            return {
              salesforce_field_label: sf.label,
              pursuit_extended_schema_field_id: null,
              override_value: entry.overrideValue ?? null,
            } as FieldMapping
          }
          return null
        })
        .filter((v): v is FieldMapping => v !== null)

      // Always include existing stage mappings (PUT is replace-all)
      const stage_mappings = (stageMappings || []).map((m) => ({
        pursuit_stage_id: m.pursuit_stage_id,
        salesforce_stage_name: m.salesforce_stage_name,
        auto_sync: m.auto_sync,
      }))

      try {
        await updateMappings({
          stage_mappings,
          pull_sync_enabled: integration?.pull_sync_enabled ?? false,
          pursuit_extended_schema_id: latestSchemaId ?? null,
          field_mappings,
        })
        toast.success(
          'Salesforce Opportunty Field Mappings updated successfully!',
        )
        onOpenChange(false)
      } catch (e) {
        console.error('Failed to update Salesforce field mappings', e)
      }
    },
    [
      fields?.fields,
      stageMappings,
      integration?.pull_sync_enabled,
      latestSchemaId,
      updateMappings,
      onOpenChange,
    ],
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    reset(buildDefaults())
  }, [onOpenChange, reset, buildDefaults])

  useEffect(() => {
    if (open) {
      reset(buildDefaults())
    }
  }, [open, reset, buildDefaults])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full md:w-[856px] lg:w-[972px] p-0 max-h-[90vh] flex flex-col overflow-hidden"
        aria-describedby={undefined}
      >
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="mb-2">
              <div className="flex items-center gap-2">
                <SalesforceIcon size={18} />
                <span>Configure Salesforce Opportunity Field Mappings</span>
              </div>
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Map your Salesforce Opportunity fields to Sweetspot Pursuit
                fields. You can select from static fields defined by Sweetspot,
                or fields that you&apos;ve defined in your Pursuit Configuration
                (if it has been set up).
              </p>
              <p>
                <span className="font-semibold">Note:</span> any time you change
                your Pursuit Configuration, you will need to update the
                Opportunity Field Mappings again.
              </p>
            </DialogDescription>
          </DialogHeader>
        </div>

        <FormProvider {...form}>
          <div className="flex-1 overflow-y-auto px-6 mt-2">
            <div className="py-3 sticky top-0 bg-white dark:bg-primary-900 z-10 grid grid-cols-6 gap-6 font-semibold text-sm font-medium text-primary-900 dark:text-primary-100 border-b border-primary-200 dark:border-primary-800">
              <div className="col-span-2">Salesforce Opportunity Field</div>
              <div className="col-span-2">Mapping Type</div>
              <div className="col-span-2 flex items-center gap-2">Value</div>
            </div>
            <div className="py-4">
              <FieldMapperBody orgId={orgId} />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-primary-200 dark:border-primary-800">
            <DialogFooter className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline-compact"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary-compact"
                onClick={handleSubmit(onSubmit)}
                disabled={isPending}
                isLoading={isPending}
              >
                Save Opportunity Field Mappings
              </Button>
            </DialogFooter>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
